import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * OnboardingPage — 5-step store onboarding wizard.
 *
 * Steps:
 *   1. 매장 정보
 *   2. 사업자 정보
 *   3. 계좌 정보
 *   4. 결제 설정
 *   5. 약관 동의
 *
 * Drafts are persisted to localStorage per step so the user can resume.
 * Final submit posts the aggregated payload to /admin/onboarding/complete.
 */

const DRAFT_KEY = 'qr.onboarding.draft';

const STORE_TYPES = ['한식', '양식', '일식', '중식', '카페/디저트', '분식', '주점/바', '패스트푸드', '기타'];
const BANKS = ['KB국민', '신한', '우리', '하나', 'NH농협', '카카오뱅크', '토스뱅크', 'IBK기업', 'SC제일', '기타'];

const STEPS = [
  { id: 1, title: '매장 정보', desc: '고객에게 보여줄 매장의 기본 정보를 입력해 주세요' },
  { id: 2, title: '사업자 정보', desc: '법적 영업을 위해 필수인 정보입니다' },
  { id: 3, title: '계좌 정보', desc: '정산금을 입금받으실 계좌입니다' },
  { id: 4, title: '결제 설정', desc: '운영 방식에 맞게 선택해 주세요' },
  { id: 5, title: '약관 동의', desc: '서비스 이용을 위해 동의가 필요합니다' },
];

const DEFAULT_FORM = {
  // Step 1
  storeName: '',
  storeType: '한식',
  storeDescription: '',
  // Step 2
  businessNumber: '',
  businessName: '',
  ceoName: '',
  businessAddress: '',
  businessPhone: '',
  // Step 3
  bankName: 'KB국민',
  accountNumber: '',
  accountHolder: '',
  // Step 4
  paymentMode: 'post', // 'post' = 후결제, 'pre' = 선결제
  useStaffCall: true,
  // Step 5
  agreeTerms: false,
  agreePrivacy: false,
  agreePayment: false,
  agreeMarketing: false,
};

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Load draft on mount.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.form) setForm({ ...DEFAULT_FORM, ...parsed.form });
        if (typeof parsed?.step === 'number') setStep(parsed.step);
      }
    } catch {}
  }, []);

  // Persist draft on change.
  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ form, step }));
    } catch {}
  }, [form, step]);

  const update = (key) => (e) => {
    const value = e?.target?.type === 'checkbox' ? e.target.checked : e?.target?.value ?? e;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const validateStep = () => {
    setError('');
    if (step === 1) {
      if (!form.storeName.trim()) return '매장명을 입력해 주세요.';
      if (!form.storeType) return '매장 유형을 선택해 주세요.';
    }
    if (step === 2) {
      if (!/^\d{3}-?\d{2}-?\d{5}$/.test(form.businessNumber.trim())) {
        return '사업자등록번호 형식이 올바르지 않습니다. (예: 123-45-67890)';
      }
      if (!form.businessName.trim()) return '상호명을 입력해 주세요.';
      if (!form.ceoName.trim()) return '대표자명을 입력해 주세요.';
      if (!form.businessAddress.trim()) return '사업장 주소를 입력해 주세요.';
      if (!form.businessPhone.trim()) return '매장 전화번호를 입력해 주세요.';
    }
    if (step === 3) {
      if (!form.bankName) return '은행을 선택해 주세요.';
      if (!/^\d{6,20}$/.test(form.accountNumber.replace(/-/g, ''))) {
        return '계좌번호를 올바르게 입력해 주세요.';
      }
      if (!form.accountHolder.trim()) return '예금주명을 입력해 주세요.';
    }
    if (step === 4) {
      if (!['pre', 'post'].includes(form.paymentMode)) return '결제 모드를 선택해 주세요.';
    }
    if (step === 5) {
      if (!form.agreeTerms || !form.agreePrivacy || !form.agreePayment) {
        return '필수 약관에 모두 동의해 주세요.';
      }
    }
    return null;
  };

  const handleNext = () => {
    const err = validateStep();
    if (err) {
      setError(err);
      return;
    }
    if (step < STEPS.length) setStep(step + 1);
  };

  const handleBack = () => {
    setError('');
    if (step > 1) setStep(step - 1);
  };

  const handleSkip = () => {
    if (!window.confirm('건너뛰면 일부 기능이 제한됩니다. 나중에 설정에서 다시 진행하실 수 있습니다. 계속하시겠습니까?')) return;
    navigate('/dashboard');
  };

  const handleSubmit = async () => {
    const err = validateStep();
    if (err) {
      setError(err);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch('/admin/onboarding/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Failed to submit onboarding');
      localStorage.removeItem(DRAFT_KEY);
      navigate('/dashboard');
    } catch (e) {
      setError('온보딩 정보 저장에 실패했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  const progress = (step / STEPS.length) * 100;
  const currentMeta = STEPS[step - 1];

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--bg)',
        padding: '40px 20px 60px',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <div style={{ width: '100%', maxWidth: 640 }}>
        {/* Header */}
        <div style={{ marginBottom: 24, textAlign: 'center' }}>
          <div
            style={{
              display: 'inline-block',
              fontSize: 12,
              fontWeight: 700,
              color: 'var(--primary)',
              background: 'var(--primary-light)',
              padding: '6px 14px',
              borderRadius: 999,
              marginBottom: 12,
              letterSpacing: '0.5px',
            }}
          >
            매장 설정 · {step}/{STEPS.length}
          </div>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.5px', marginBottom: 6 }}>
            {currentMeta.title}
          </h1>
          <p style={{ fontSize: 14, color: 'var(--text-tertiary)' }}>{currentMeta.desc}</p>
        </div>

        {/* Progress bar */}
        <div
          style={{
            height: 6,
            background: 'var(--border)',
            borderRadius: 999,
            marginBottom: 28,
            overflow: 'hidden',
          }}
          role="progressbar"
          aria-valuenow={step}
          aria-valuemin={1}
          aria-valuemax={STEPS.length}
        >
          <div
            style={{
              width: `${progress}%`,
              height: '100%',
              background: 'var(--primary)',
              borderRadius: 999,
              transition: 'width 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
            }}
          />
        </div>

        {/* Step indicators */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 32, gap: 8 }}>
          {STEPS.map((s) => {
            const done = s.id < step;
            const active = s.id === step;
            return (
              <div
                key={s.id}
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 6,
                  cursor: done ? 'pointer' : 'default',
                }}
                onClick={() => done && setStep(s.id)}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    background: active || done ? 'var(--primary)' : 'var(--border)',
                    color: active || done ? '#fff' : 'var(--text-tertiary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: 14,
                    transition: 'all 0.2s',
                  }}
                >
                  {done ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : (
                    s.id
                  )}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: active ? 'var(--text)' : 'var(--text-tertiary)',
                    fontWeight: active ? 700 : 500,
                    textAlign: 'center',
                  }}
                >
                  {s.title}
                </div>
              </div>
            );
          })}
        </div>

        {/* Card */}
        <div
          style={{
            background: 'var(--surface)',
            borderRadius: 20,
            padding: 32,
            boxShadow: 'var(--shadow-md, 0 4px 12px rgba(0,0,0,0.06))',
            marginBottom: 20,
          }}
        >
          {step === 1 && <Step1 form={form} update={update} />}
          {step === 2 && <Step2 form={form} update={update} />}
          {step === 3 && <Step3 form={form} update={update} setField={setField} />}
          {step === 4 && <Step4 form={form} setField={setField} />}
          {step === 5 && <Step5 form={form} update={update} />}

          {error && (
            <div
              role="alert"
              style={{
                marginTop: 20,
                padding: '12px 16px',
                background: 'var(--danger-light)',
                color: 'var(--danger)',
                borderRadius: 12,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {error}
            </div>
          )}
        </div>

        {/* Nav buttons */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
          {step > 1 && (
            <button
              type="button"
              onClick={handleBack}
              style={{
                flex: 1,
                padding: '16px 20px',
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 12,
                fontSize: 15,
                fontWeight: 700,
                color: 'var(--text-secondary)',
                cursor: 'pointer',
              }}
            >
              이전
            </button>
          )}
          {step < STEPS.length ? (
            <button
              type="button"
              onClick={handleNext}
              style={{
                flex: 2,
                padding: '16px 20px',
                background: 'var(--primary)',
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                fontSize: 15,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              다음
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              style={{
                flex: 2,
                padding: '16px 20px',
                background: 'var(--primary)',
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                fontSize: 15,
                fontWeight: 700,
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.6 : 1,
              }}
            >
              {submitting ? '저장 중...' : '설정 완료'}
            </button>
          )}
        </div>

        {/* Skip */}
        <div style={{ textAlign: 'center' }}>
          <button
            type="button"
            onClick={handleSkip}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-tertiary)',
              fontSize: 13,
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            나중에 설정하기 (일부 기능 제한)
          </button>
        </div>
      </div>
    </div>
  );
}

/* ========== Step components ========== */

function StepIcon({ children }) {
  return (
    <div
      style={{
        width: 56,
        height: 56,
        borderRadius: 16,
        background: 'var(--primary-light)',
        color: 'var(--primary)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 16,
      }}
    >
      {children}
    </div>
  );
}

function Label({ children, required }) {
  return (
    <label
      style={{
        display: 'block',
        fontSize: 13,
        fontWeight: 700,
        color: 'var(--text)',
        marginBottom: 8,
      }}
    >
      {children}
      {required && <span style={{ color: 'var(--danger)', marginLeft: 4 }}>*</span>}
    </label>
  );
}

const inputCss = {
  width: '100%',
  padding: '14px 16px',
  background: 'var(--border)',
  border: '1px solid transparent',
  borderRadius: 12,
  fontSize: 15,
  color: 'var(--text)',
  outline: 'none',
  fontFamily: 'inherit',
};

function Step1({ form, update }) {
  return (
    <div>
      <StepIcon>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      </StepIcon>
      <div style={{ marginBottom: 20 }}>
        <Label required>매장명</Label>
        <input type="text" value={form.storeName} onChange={update('storeName')} placeholder="예: 맛있는 한식당" style={inputCss} />
      </div>
      <div style={{ marginBottom: 20 }}>
        <Label required>매장 유형</Label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
          {STORE_TYPES.map((t) => {
            const active = form.storeType === t;
            return (
              <button
                key={t}
                type="button"
                onClick={() => update('storeType')({ target: { value: t } })}
                style={{
                  padding: '12px 8px',
                  background: active ? 'var(--primary-light)' : 'var(--bg)',
                  border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
                  borderRadius: 10,
                  color: active ? 'var(--primary)' : 'var(--text-secondary)',
                  fontSize: 13,
                  fontWeight: active ? 700 : 500,
                  cursor: 'pointer',
                }}
              >
                {t}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <Label>매장 소개</Label>
        <textarea
          value={form.storeDescription}
          onChange={update('storeDescription')}
          placeholder="고객에게 매장을 소개해 주세요 (선택)"
          rows={4}
          style={{ ...inputCss, resize: 'vertical', minHeight: 96 }}
        />
      </div>
    </div>
  );
}

function Step2({ form, update }) {
  return (
    <div>
      <StepIcon>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="9" y1="13" x2="15" y2="13" />
          <line x1="9" y1="17" x2="15" y2="17" />
        </svg>
      </StepIcon>
      <div style={{ marginBottom: 16 }}>
        <Label required>사업자등록번호</Label>
        <input type="text" value={form.businessNumber} onChange={update('businessNumber')} placeholder="123-45-67890" style={inputCss} />
      </div>
      <div style={{ marginBottom: 16 }}>
        <Label required>상호</Label>
        <input type="text" value={form.businessName} onChange={update('businessName')} placeholder="사업자 등록상 상호" style={inputCss} />
      </div>
      <div style={{ marginBottom: 16 }}>
        <Label required>대표자명</Label>
        <input type="text" value={form.ceoName} onChange={update('ceoName')} placeholder="홍길동" style={inputCss} />
      </div>
      <div style={{ marginBottom: 16 }}>
        <Label required>사업장 주소</Label>
        <input type="text" value={form.businessAddress} onChange={update('businessAddress')} placeholder="도로명 주소로 입력" style={inputCss} />
      </div>
      <div>
        <Label required>매장 전화번호</Label>
        <input type="tel" value={form.businessPhone} onChange={update('businessPhone')} placeholder="02-123-4567" style={inputCss} />
      </div>
    </div>
  );
}

function Step3({ form, update, setField }) {
  return (
    <div>
      <StepIcon>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <rect x="2" y="5" width="20" height="14" rx="2" />
          <line x1="2" y1="10" x2="22" y2="10" />
        </svg>
      </StepIcon>
      <div style={{ marginBottom: 16 }}>
        <Label required>은행</Label>
        <select value={form.bankName} onChange={update('bankName')} style={{ ...inputCss, appearance: 'none' }}>
          {BANKS.map((b) => (
            <option key={b} value={b}>{b}</option>
          ))}
        </select>
      </div>
      <div style={{ marginBottom: 16 }}>
        <Label required>계좌번호</Label>
        <input
          type="text"
          value={form.accountNumber}
          onChange={update('accountNumber')}
          placeholder="하이픈 없이 숫자만 입력"
          style={inputCss}
        />
      </div>
      <div>
        <Label required>예금주</Label>
        <input type="text" value={form.accountHolder} onChange={update('accountHolder')} placeholder="예금주 성함" style={inputCss} />
      </div>
      <div
        style={{
          marginTop: 20,
          padding: 14,
          background: 'var(--warning-light, #FFF5E0)',
          borderRadius: 10,
          fontSize: 12,
          color: 'var(--warning, #FF9500)',
          fontWeight: 600,
          lineHeight: 1.6,
        }}
      >
        정산금은 매주 수요일에 사업자 명의의 계좌로 입금됩니다. 예금주명은 사업자등록증과 일치해야 합니다.
      </div>
    </div>
  );
}

function Step4({ form, setField }) {
  return (
    <div>
      <StepIcon>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="6" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      </StepIcon>

      <Label required>결제 모드</Label>
      <div style={{ display: 'grid', gap: 10, marginBottom: 24 }}>
        <ModeCard
          active={form.paymentMode === 'post'}
          onClick={() => setField('paymentMode', 'post')}
          title="후결제"
          desc="식사 후 고객이 결제합니다. 주문 수정이 자유롭고 추가 주문이 가능합니다."
        />
        <ModeCard
          active={form.paymentMode === 'pre'}
          onClick={() => setField('paymentMode', 'pre')}
          title="선결제"
          desc="주문과 동시에 결제합니다. 노쇼/미결제가 없고 정산이 빠릅니다."
        />
      </div>

      <Label>직원 호출 기능</Label>
      <div
        onClick={() => setField('useStaffCall', !form.useStaffCall)}
        style={{
          padding: '14px 16px',
          border: `1px solid ${form.useStaffCall ? 'var(--primary)' : 'var(--border)'}`,
          borderRadius: 12,
          background: form.useStaffCall ? 'var(--primary-light)' : 'var(--surface)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
        }}
      >
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>직원 호출 사용</div>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 }}>
            고객이 QR 화면에서 직원을 부를 수 있습니다
          </div>
        </div>
        <div
          style={{
            width: 44,
            height: 26,
            background: form.useStaffCall ? 'var(--primary)' : 'var(--text-quaternary)',
            borderRadius: 999,
            position: 'relative',
            transition: 'background 0.2s',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 3,
              left: form.useStaffCall ? 21 : 3,
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: '#fff',
              transition: 'left 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }}
          />
        </div>
      </div>
    </div>
  );
}

function ModeCard({ active, onClick, title, desc }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign: 'left',
        padding: '16px 18px',
        border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
        background: active ? 'var(--primary-light)' : 'var(--surface)',
        borderRadius: 12,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
      }}
    >
      <div
        style={{
          width: 20,
          height: 20,
          borderRadius: '50%',
          border: `2px solid ${active ? 'var(--primary)' : 'var(--text-quaternary)'}`,
          flexShrink: 0,
          marginTop: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {active && <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--primary)' }} />}
      </div>
      <div>
        <div style={{ fontSize: 15, fontWeight: 700, color: active ? 'var(--primary)' : 'var(--text)', marginBottom: 2 }}>
          {title}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', lineHeight: 1.6 }}>{desc}</div>
      </div>
    </button>
  );
}

function Step5({ form, update }) {
  const agreements = [
    { key: 'agreeTerms', label: '서비스 이용약관 동의', required: true, link: '/terms-admin' },
    { key: 'agreePrivacy', label: '개인정보처리방침 동의', required: true, link: '/privacy' },
    { key: 'agreePayment', label: '결제대행 서비스 이용 동의', required: true, link: '/terms' },
    { key: 'agreeMarketing', label: '마케팅 정보 수신 동의 (선택)', required: false },
  ];

  const allRequired = agreements.filter((a) => a.required).every((a) => form[a.key]);
  const allChecked = agreements.every((a) => form[a.key]);

  const toggleAll = () => {
    const next = !allChecked;
    agreements.forEach((a) => update(a.key)({ target: { type: 'checkbox', checked: next } }));
  };

  return (
    <div>
      <StepIcon>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M9 12l2 2 4-4" />
          <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
        </svg>
      </StepIcon>

      <div
        onClick={toggleAll}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '14px 16px',
          background: 'var(--primary-light)',
          borderRadius: 12,
          marginBottom: 16,
          cursor: 'pointer',
        }}
      >
        <Checkbox checked={allChecked} />
        <div style={{ flex: 1, fontSize: 15, fontWeight: 700, color: 'var(--primary)' }}>
          전체 동의하기
        </div>
      </div>

      <div style={{ display: 'grid', gap: 10 }}>
        {agreements.map((a) => (
          <div
            key={a.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 16px',
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 10,
            }}
          >
            <div onClick={() => update(a.key)({ target: { type: 'checkbox', checked: !form[a.key] } })} style={{ cursor: 'pointer' }}>
              <Checkbox checked={form[a.key]} />
            </div>
            <div style={{ flex: 1, fontSize: 14, color: 'var(--text)' }}>
              <span style={{ color: a.required ? 'var(--danger)' : 'var(--text-tertiary)', fontWeight: 700, marginRight: 4 }}>
                {a.required ? '[필수]' : '[선택]'}
              </span>
              {a.label}
            </div>
            {a.link && (
              <a
                href={a.link}
                target="_blank"
                rel="noreferrer"
                style={{ fontSize: 12, color: 'var(--text-tertiary)', textDecoration: 'underline' }}
              >
                보기
              </a>
            )}
          </div>
        ))}
      </div>

      {!allRequired && (
        <div style={{ marginTop: 14, fontSize: 12, color: 'var(--text-tertiary)' }}>
          서비스 이용을 위해서는 필수 항목에 모두 동의하셔야 합니다.
        </div>
      )}
    </div>
  );
}

function Checkbox({ checked }) {
  return (
    <div
      style={{
        width: 22,
        height: 22,
        borderRadius: 6,
        border: `2px solid ${checked ? 'var(--primary)' : 'var(--text-quaternary)'}`,
        background: checked ? 'var(--primary)' : 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {checked && (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )}
    </div>
  );
}
